import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import messages from "../../../assets/locale/messages";
import { UIButton, UIIcon } from "../../ui";
import { clx } from "../../../utils/Helpers";
import { EDIT_REQUEST_BANNER_STATUS } from "./constants";

import "./_EditRequestBanner.scss";

const STATUS_CONFIG = {
  [EDIT_REQUEST_BANNER_STATUS.APPROVED]: {
    theme: "success",
    Icon: CheckCircleOutlineIcon,
    messageKey: "approved",
    showDismiss: true,
  },
  [EDIT_REQUEST_BANNER_STATUS.DECLINED]: {
    theme: "danger",
    Icon: CancelOutlinedIcon,
    messageKey: "declined",
    showViewDetails: true,
  },
  [EDIT_REQUEST_BANNER_STATUS.PENDING]: {
    theme: "pending",
    icon: "infoCircle",
    messageKey: "pending",
    showCancel: true,
    showEdit: true,
  },
  [EDIT_REQUEST_BANNER_STATUS.REVIEWING]: {
    theme: "reviewing",
    icon: "infoCircle",
    messageKey: "reviewing",
    showDismiss: true,
  },
};

const EditRequestBanner = ({
  status,
  message,
  onDismiss,
  onEdit,
  onCancel,
  onViewDetails,
  loading,
  className,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang] || {};
  const copy = shared?.editRequestBanner;

  const config = STATUS_CONFIG[status];
  if (!config || !copy) return null;

  const { theme, Icon, icon, messageKey } = config;
  const resolvedMessage = message || copy[messageKey];
  const actions = copy.actions || {};
  const isInfoVariant = icon === "infoCircle";

  const renderDismiss = config.showDismiss && typeof onDismiss === "function";
  const renderEdit = config.showEdit && typeof onEdit === "function";
  const renderCancel = config.showCancel && typeof onCancel === "function";
  const renderViewDetails =
    config.showViewDetails && typeof onViewDetails === "function";

  return (
    <div
      role="status"
      aria-live="polite"
      className={clx(
        "edit-request-banner",
        `edit-request-banner--${theme}`,
        isInfoVariant && "edit-request-banner--info",
        className,
      )}
    >
      <div className="edit-request-banner__lead">
        <div className="edit-request-banner__icon" aria-hidden>
          {isInfoVariant ? (
            <UIIcon
              name="infoCircle"
              size={20}
              color="var(--color-background-default, #ffffff)"
              className="edit-request-banner__icon-svg"
            />
          ) : (
            <Icon fontSize="small" />
          )}
        </div>

        <p className="edit-request-banner__message">{resolvedMessage}</p>
      </div>

      <div className="edit-request-banner__actions">
        {renderCancel && (
          <UIButton
            type="button"
            variant="link"
            size="small"
            onClick={onCancel}
            disabled={loading}
          >
            {actions.cancelRequest}
          </UIButton>
        )}

        {renderViewDetails && (
          <UIButton
            type="button"
            variant="link"
            size="small"
            onClick={onViewDetails}
            disabled={loading}
          >
            {actions.viewDetails}
          </UIButton>
        )}

        {renderEdit && (
          <UIButton
            type="button"
            variant="primary"
            size="small"
            onClick={onEdit}
            disabled={loading}
          >
            {actions.editRequest}
          </UIButton>
        )}

        {renderDismiss && (
          <UIButton
            type="button"
            variant="link"
            size="small"
            onClick={onDismiss}
            disabled={loading}
          >
            {actions.dismiss}
          </UIButton>
        )}
      </div>
    </div>
  );
};

EditRequestBanner.propTypes = {
  status: PropTypes.oneOf(Object.values(EDIT_REQUEST_BANNER_STATUS)).isRequired,
  message: PropTypes.node,
  onDismiss: PropTypes.func,
  onEdit: PropTypes.func,
  onCancel: PropTypes.func,
  onViewDetails: PropTypes.func,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

EditRequestBanner.defaultProps = {
  message: undefined,
  onDismiss: undefined,
  onEdit: undefined,
  onCancel: undefined,
  onViewDetails: undefined,
  loading: false,
  className: "",
};

export default EditRequestBanner;
